// ---------------------------------------------------------------------------
// Error classes
// ---------------------------------------------------------------------------

export class CopeError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'CopeError'
  }
}

export class CopeApiError extends CopeError {
  readonly code: ApiErrorCode
  readonly status: number
  readonly field?: string
  readonly requestId: string | null
  /** Seconds the server asked the caller to wait (`Retry-After`), or null. */
  readonly retryAfter: number | null
  readonly errors: ReadonlyArray<{ readonly code: string; readonly message: string; readonly field?: string }>

  constructor(
    status: number,
    errors: Array<{ code: string; message: string; field?: string }>,
    requestId?: string | null,
    retryAfter?: number | null,
  ) {
    const first = errors[0]
    super(first?.message ?? `API error ${status}`)
    this.name = 'CopeApiError'
    this.status = status
    this.code = (first?.code ?? 'unknown') as ApiErrorCode
    this.field = first?.field
    this.requestId = requestId ?? null
    this.retryAfter = retryAfter ?? null
    this.errors = errors
  }
}

export class CopeNetworkError extends CopeError {
  constructor(message: string) {
    super(message)
    this.name = 'CopeNetworkError'
  }
}

export class CopeCartExpiredError extends CopeError {
  readonly state: CartState

  constructor(state: CartState) {
    super(`Cart is ${state} and cannot be used`)
    this.name = 'CopeCartExpiredError'
    this.state = state
  }
}

// ---------------------------------------------------------------------------
// Error codes — known codes + string escape hatch
// ---------------------------------------------------------------------------

export type ApiErrorCode =
  // The codes the cart-API endpoints this SDK calls can answer.
  // tests/error-codes-agreement.test.ts holds this list to the literal codes in a
  // listed set of cope-core sources, in both directions; a code built at runtime
  // or emitted outside that set is not seen.
  | 'unauthorized'
  | 'invalid_sdk_key'
  | 'cart_id_mismatch'
  | 'not_found'
  | 'validation_error'
  | 'forbidden_field'
  | 'business_checkout_hold'
  | 'cart_not_active'
  | 'unsupported_currency'
  | 'unsupported_checkout_locale'
  | 'checkout_locale_not_allowed'
  | 'duplicate_line'
  | 'not_saleable'
  | 'product_unavailable'
  | 'insufficient_stock'
  | 'mixed_seller_cart'
  | 'mixed_vat_mode_cart'
  | 'empty_cart'
  | 'below_minimum_charge'
  | 'above_maximum_charge'
  | 'missing_tax_location'
  | 'cart_not_repriceable'
  | 'tax_address_invalid'
  | 'tax_request_rejected'
  | 'tax_service_unavailable'
  | 'invalid_redirect_url'
  | 'embed_origin_not_allowed'
  | 'price_changed'
  | 'checkout_expired'
  | 'checkout_not_cancellable'
  | 'checkout_payment_in_flight'
  | 'idempotency_conflict'
  | 'idempotency_body_mismatch'
  // A refused promo code (422), PromoCodes::Validate's reasons. `not_found` here
  // means no such code; it shares the name of the 404 code above.
  | 'inactive'
  | 'not_started'
  | 'expired'
  | 'exhausted'
  | 'no_applicable_items'
  | 'currency_not_supported'
  | 'promo_not_supported_for_recurring'
  | 'offer_sourced_no_promo'
  | 'promo_locked_during_payment'
  // Answered by cope-edge itself, in its `{error, request_id}` envelope.
  | 'payload_too_large'
  | 'rate_limited'
  | 'upstream_unavailable'
  | 'gateway_timeout'
  // Answered when the buyer's payment session is created on COPE's hosted
  // checkout page — an endpoint this SDK does not call. Kept for code that
  // switches on them; no SDK method returns them.
  | 'incomplete_buyer_identity'
  | 'incomplete_shipping_address'
  | 'missing_consent'
  | 'missing_withdrawal_waiver'
  | 'checkout_not_open'
  | 'payment_provider_error'
  | (string & {}) // escape hatch for unknown codes

// ---------------------------------------------------------------------------
// Cart
// ---------------------------------------------------------------------------

export type CartState =
  | 'active'
  | 'checkout_in_progress'
  | 'ordered'
  | 'expired'
  | 'abandoned'
  | 'merged'

export interface OneClickChargeConsentApplicability {
  readonly applicable: boolean
  readonly terms_version: string | null
}

export interface Cart {
  readonly id: string
  readonly state: CartState
  readonly version: number
  readonly currency: string
  readonly charge_currency: string
  /** Present only in the `createCart()` response. The SDK persists it to localStorage automatically. */
  readonly cart_secret?: string
  readonly lines: readonly CartLine[]
  /** Present only on a `setBuyerIdentity()` response that dropped a field, e.g. a malformed phone. */
  readonly field_errors?: readonly FieldError[]
  /**
   * `getCart()` masks the email (`j***n@example.com`); the `setBuyerIdentity()`
   * response carries it as saved.
   *
   * Absent where cope-core does not report it — the reprice and promo-code
   * responses — which is not the same as `null` (no buyer recorded yet).
   */
  readonly buyer_identity?: BuyerIdentity | null
  readonly totals: CartTotals | null
  readonly locale: string
  readonly applied_promo_code: AppliedPromoCode | null
  /** Backend-owned OTO candidate; absent while older backends roll forward. */
  readonly one_click_charge_consent?: OneClickChargeConsentApplicability | null
}

// ---------------------------------------------------------------------------
// Cart Line
// ---------------------------------------------------------------------------

export interface CartLine {
  /** The line's id: an integer (the cart API renders `cart_lines.id`, a bigint). */
  readonly id: number
  readonly cart_id: string
  readonly product_id: number
  readonly plan_id: number | null
  readonly quantity: number
  readonly price_snapshot: PriceSnapshot | null
  /** The product's name, present before the line is repriced (`price_snapshot` is null until then). */
  readonly product_name?: string | null
  readonly product_uuid: string
  readonly stale?: boolean
  readonly requires_shipping?: boolean
  readonly trial_days?: number
  readonly payment_methods: readonly string[]
  readonly plan_type: string | null
}

export interface PriceSnapshot {
  readonly unit_price_minor_units: number
  readonly currency: string
  readonly charge_currency: string
  readonly product_name: string
  readonly plan_name: string | null
  readonly captured_at: string
}

// ---------------------------------------------------------------------------
// Buyer Identity
// ---------------------------------------------------------------------------

export interface Address {
  readonly line1: string
  readonly line2: string | null
  readonly city: string
  readonly region?: string | null
  readonly postal_code: string
  readonly country: string
}

/** The promo code on the cart and what it takes off (apps/cope-core CartBlueprint `applied_promo_code`). */
export interface AppliedPromoCode {
  readonly id: number
  readonly code: string
  readonly discount_total_minor_units: number
  /** True when the discount was trimmed to keep the order above the minimum charge. */
  readonly capped_to_minimum_charge: boolean
}

export interface BuyerIdentity {
  readonly email: string
  readonly country: string
  readonly postal_code: string | null
  readonly company: string | null
  readonly buyer_type: 'b2b' | 'b2c' | null
  readonly billing_address: Address | null
  readonly shipping_address: Address | null
  readonly consents: readonly Consent[]
}

export interface Consent {
  readonly type: string
  readonly granted_at: string
  readonly ip: string
}

// ---------------------------------------------------------------------------
// Totals
// ---------------------------------------------------------------------------

export type TotalsFinality = 'estimate' | 'final'

export interface CartTotals {
  readonly subtotal_minor_units: number
  readonly tax_minor_units: number
  readonly shipping_minor_units: number
  readonly discount_minor_units: number
  readonly total_minor_units: number
  /** Currency the *_minor_units are denominated in. Always equals `charge_currency`. */
  readonly currency: string
  /** Currency that will be billed (Stripe PaymentIntent.currency). */
  readonly charge_currency: string
  readonly finality: TotalsFinality
  /** Buyer's selected presentment currency, when different from `charge_currency`.
   *  Paired with `presentment_exchange_rate`; both present or both absent.
   *  To display amounts in presentment, multiply each `*_minor_units` by `presentment_exchange_rate`. */
  readonly presentment_currency?: string
  readonly presentment_exchange_rate?: number
}

// ---------------------------------------------------------------------------
// Product (read-only, for getProduct)
// ---------------------------------------------------------------------------

export interface SdkProduct {
  readonly uuid: string
  readonly name: string
  readonly headline: string | null
  readonly product_type: string
  readonly currency: string
  readonly payment_methods: readonly string[]
  readonly images: readonly SdkProductImage[]
  readonly payment_plans: readonly SdkPaymentPlan[]
}

export interface SdkProductImage {
  readonly id: number
  readonly image_url: string
}

export interface SdkPaymentPlan {
  readonly id: number
  readonly plan_type: 'one_time' | 'subscription' | 'installment'
  readonly position: number
  readonly currency: string
  readonly display_name: string | null
  /** Price as a decimal string, e.g. "24.99" (dollars, not cents despite the field name) */
  readonly first_payment_amount_cents: string
  readonly next_payments_amount_cents: string | null
  readonly next_payments_count: number | null
  readonly shipping_price_cents: string
  readonly charge_shipping_once: boolean
  readonly shipping_depend_on_quantity: boolean
  readonly interval: 'day' | 'week' | 'month' | 'year' | null
  readonly interval_count: number | null
  readonly second_payment_in: number | null
  readonly vanity_prices: readonly SdkVanityPrice[]
}

export interface SdkVanityPrice {
  readonly id: number
  readonly currency: string
  readonly first_payment_amount_cents: string
  readonly next_payments_amount_cents: string | null
}

// ---------------------------------------------------------------------------
// Checkout
// ---------------------------------------------------------------------------

export type CheckoutStatus = 'open' | 'completed' | 'expired' | 'cancelled'

export interface CheckoutCreateEmbedPolicy {
  readonly allowed: true
  readonly parent_origin: string
}

export interface CheckoutCreateResponse {
  readonly checkout_id: string
  readonly checkout_url: string
  readonly embed?: CheckoutCreateEmbedPolicy
  readonly status: CheckoutStatus
  readonly expires_at: string
  readonly totals: CartTotals
  readonly cart_version: number
}

// ---------------------------------------------------------------------------
// SDK Options & Payloads
// ---------------------------------------------------------------------------

export interface CopeOptions {
  readonly publishableKey: string
  readonly baseUrl?: string
  readonly checkoutBaseUrl?: string
  /**
   * Where the active cart's id and secret are kept between page loads.
   * `'localStorage'` (default) is right for one buyer in one browser. Use `'none'`
   * on a server, where one process serves many buyers: a process-wide store would
   * hand one buyer's cart secret to the next client. A client also holds its cart
   * secret in memory and sends it on every request, so a server needs one client
   * per buyer (or per request with `cart`), never one shared client.
   */
  readonly persistence?: 'localStorage' | 'none'
  /**
   * Act on an existing cart whose credentials the caller already holds. Implies
   * `persistence: 'none'`: such a client never reads, writes or clears stored carts.
   */
  readonly cart?: { readonly id: string; readonly secret: string }
}

/** @deprecated Use `CopeOptions`. */
export type CopeCartOptions = CopeOptions

export interface CheckoutOptions {
  /**
   * Sent as the Idempotency-Key. The cart API replays its stored answer to a
   * repeated key and identical body, so a caller that may re-send the same
   * checkout (an agent retrying a tool call) gets the same checkout back instead
   * of `cart_not_active` from a cart the first call already locked. While the first
   * call is still being processed, a repeat gets 409 `idempotency_conflict`; wait
   * briefly and repeat again.
   */
  readonly idempotencyKey?: string
}

export interface CreateCartPayload {
  readonly currency: string
  readonly locale?: string
  readonly attribution?: CartAttribution
  readonly metadata?: CartMetadata
}

export interface CartAttribution {
  readonly affiliate_id?: string
  readonly promo_code?: string
  readonly utm_source?: string
  readonly utm_medium?: string
  readonly utm_campaign?: string
  readonly utm_content?: string
  readonly utm_term?: string
  readonly landing_url?: string
  readonly referrer?: string
  readonly captured_at?: string
}

export type CartMetadataValue =
  | string
  | number
  | boolean
  | null
  | readonly CartMetadataValue[]
  | { readonly [key: string]: CartMetadataValue }

export interface CartMetadata {
  readonly [key: string]: CartMetadataValue
}

export interface AddLinePayload {
  /** Product UUID string — resolved to integer FK in API response (CartLine.product_id) */
  readonly product_id: string
  /** Optional payment plan ID. Omit it to let the API select the product's default plan. */
  readonly plan_id?: number
  readonly quantity?: number
}

export interface UpdateLinePayload {
  readonly quantity?: number
  readonly plan_id?: number
}

export interface UpdateBuyerIdentityPayload {
  readonly email?: string
  readonly first_name?: string
  readonly last_name?: string
  readonly country?: string
  readonly postal_code?: string
  readonly company?: string
  readonly buyer_type?: 'b2b' | 'b2c'
  readonly vat_id?: string
  readonly billing_address?: Partial<Address>
  readonly shipping_address?: Partial<Address>
  /**
   * Not blocking: a malformed phone is dropped and the rest of the identity is
   * saved, and the response says so in `field_errors`.
   */
  readonly phone?: string
  /** The buyer's checkout language; a locale the business has not enabled is ignored. */
  readonly locale?: string
  readonly consents?: ReadonlyArray<{ readonly type: string }>
}

export interface CheckoutPayload {
  readonly success_url?: string
  readonly cancel_url?: string
  readonly embed_origin?: string
  /**
   * Recorded on the checkout when sent; not required to create it. When the
   * buyer's payment session is created, `buyer_tos` is required, and so is
   * `withdrawal_waiver` for a cart holding a product that does not ship (a digital
   * product). The hosted checkout page asks the buyer for both; send them here
   * only when the buyer has already given them on your page.
   */
  readonly consents?: ReadonlyArray<{ readonly type: string; readonly version?: string }>
}

/**
 * The minimum a checkout has to expose to be mounted in an iframe.
 *
 * `mountCheckout` reads exactly three things: `embedCheckoutUrl` (the frame's
 * src, and the source of the handshake token), `embedOrigin` (checked against
 * the hosting page), and `checkoutUrl` — the last only for `fallback:
 * 'redirect'`, which refuses to run without it rather than deriving one. It
 * never reads `checkoutId`, `totals`, `status` or `expiresAt`.
 *
 * Asking for a whole `CheckoutResult` therefore asked for four fields it does
 * not use, which shut out the caller that has no CheckoutResult to give: an
 * offer is created server-side and its API answers with `embed_url` +
 * `embed_origin`, no cart and no totals in sight. Integrators were left
 * inventing a `checkoutId` to satisfy a type, or hand-rolling the postMessage
 * handshake the SDK exists to own.
 *
 * `mountCheckout` accepts `CheckoutResult | MountableCheckout` rather than this
 * type alone, and the union is load-bearing: TypeScript excess-property-checks
 * an object LITERAL against its parameter type, so narrowing the parameter to
 * this interface would have rejected the seven-field literal integrators write
 * inline today — a compile break dressed up as a widening. Keeping
 * `CheckoutResult` in the union gives that literal an arm to match.
 */
export interface MountableCheckout {
  readonly embedCheckoutUrl: string
  /** Required: `mountCheckout` refuses to mount without it. */
  readonly embedOrigin: string
  /** Required only by `fallback: 'redirect'`; for an offer, pass `public_url`. */
  readonly checkoutUrl?: string
}

export interface CheckoutResult {
  readonly checkoutId: string
  readonly checkoutUrl: string
  readonly embedCheckoutUrl: string
  readonly embedOrigin?: string
  readonly status: CheckoutStatus
  readonly expiresAt: string
  readonly totals: CartTotals
}

export interface PrefilledCheckoutLineInput {
  readonly productId: string
  /** Optional payment plan ID. Omit it to let the API select the product's default plan. */
  readonly planId?: number
  readonly quantity?: number
}

interface PrefilledCheckoutBaseInput {
  /**
   * Stable caller-side reference, e.g. an event sales session ID. The SDK uses
   * it for the backend Idempotency-Key and stores it as cart metadata. Distinct
   * references always get distinct keys: up to 120 characters of
   * `[A-Za-z0-9._:-]` are used as is, anything else is SHA-256'd whole. A replay
   * returns the stored handoff only for the cart API's 24-hour idempotency TTL;
   * after that the same reference creates a new cart and checkout.
   */
  readonly externalReference: string
  readonly currency: string
  readonly locale?: string
  readonly attribution?: CartAttribution
  /**
   * Non-sensitive reconciliation metadata. `external_reference` and
   * `intended_payment_method` are reserved metadata keys populated from the
   * matching top-level SDK fields.
   */
  readonly metadata?: CartMetadata
  readonly intendedPaymentMethod?: string
  /**
   * Let the hosted checkout page collect the buyer's email, country and postal
   * code instead of requiring them in `buyerIdentity`. Only with
   * `intendedPaymentMethod: 'hosted'`; the API refuses it otherwise.
   */
  readonly collectBuyerIdentityAtCheckout?: boolean
  readonly buyerIdentity: UpdateBuyerIdentityPayload
  readonly checkout: CheckoutPayload
}

export type PrefilledCheckoutInput = PrefilledCheckoutBaseInput & (
  | {
    readonly line: PrefilledCheckoutLineInput
    readonly lines?: never
  }
  | {
    readonly line?: never
    readonly lines: readonly [PrefilledCheckoutLineInput, ...PrefilledCheckoutLineInput[]]
  }
)

export interface PrefilledCheckoutResult {
  /**
   * Cart ID for logging and reconciliation only. The prefilled handoff response
   * intentionally excludes `cart_secret`, so this SDK instance cannot use this
   * cart ID for follow-up mutations such as `addLine`, `reprice`, or `checkout`.
   */
  readonly cartId: string
  readonly cartVersion: number
  readonly checkoutId: string
  readonly checkoutToken: string
  readonly checkoutUrl: string
  readonly embedCheckoutUrl: string
  readonly embedOrigin?: string
  readonly status: CheckoutStatus
  readonly expiresAt: string
  readonly totals: CartTotals
  readonly intendedPaymentMethod?: string
  /** Non-blocking notices, e.g. a malformed phone that was dropped. Absent when there are none. */
  readonly fieldErrors?: readonly FieldError[]
}

/** A non-blocking notice the API attaches to a successful response. */
export interface FieldError {
  readonly code: string
  readonly message: string
  readonly field: string
}

// ---------------------------------------------------------------------------
// Checkout embed postMessage protocol — consumer half of a two-package contract
// ---------------------------------------------------------------------------
//
// The producer is the hosted checkout embed bridge
// (`apps/cope-frontend/lib/checkout/embed-protocol.ts` +
// `apps/cope-frontend/app/checkout/embed/[token]/HostedCheckoutEmbedBridge.tsx`).
// A status the bridge emits but this list omits is silently dropped by
// `parseCheckoutEmbedEvent` — the parent frame then sees nothing at all, not
// even an error. The two lists are asserted equal by
// `tests/checkout-embed-protocol-contract.test.ts` (and by its mirror in the
// frontend suite, so either package's CI lane catches a divergence).
//
// Do not add a value here without adding a producer on the bridge side, and do
// not add one there without adding it here.

export const CHECKOUT_EMBED_ERROR_CODES = [
  'not_found',
  'load_failed',
  'embed_not_allowed',
  'confirm_failed',
] as const

export type CheckoutEmbedErrorCode = (typeof CHECKOUT_EMBED_ERROR_CODES)[number]
export type CheckoutEmbedFallbackMode = 'redirect' | 'error'

/**
 * Terminal states the mounted checkout can report.
 *
 * - `completed` — the order was placed in this session (live payment success).
 * - `already_completed` — the token resolved to a checkout that was paid
 *   *before* this iframe mounted (refresh after success, bookmark, re-opened
 *   offer link). The parent must not re-run fulfilment, so it does NOT fire
 *   `onSuccess`.
 * - `processing` — an asynchronous rail (Klarna/iDEAL/BNPL/SEPA) accepted the
 *   payment but Cart's authoritative order state is still `processing`. Not a
 *   success: the parent should wait for its own server-side signal.
 * - `expired` / `cancelled` — the checkout or its offer is no longer payable.
 */
export const CHECKOUT_EMBED_TERMINAL_STATUSES = [
  'expired',
  'completed',
  'already_completed',
  'cancelled',
  'processing',
] as const

export type CheckoutEmbedTerminalStatus = (typeof CHECKOUT_EMBED_TERMINAL_STATUSES)[number]

export interface CheckoutEmbedErrorPayload {
  readonly code: CheckoutEmbedErrorCode
  readonly retryable: boolean
}

export interface CheckoutEmbedTerminalPayload {
  readonly status: CheckoutEmbedTerminalStatus
}

export interface CheckoutEmbedResizePayload {
  readonly height: number
}

export interface CheckoutEmbedEventMetadata {
  readonly messageId?: string
  readonly timestamp?: string
}

// ---------------------------------------------------------------------------
// Post-Purchase Offer (OTO) — native post-purchase upsell/downsell flow
// ---------------------------------------------------------------------------
//
// These types mirror the backend post-purchase offer/decision contract for
// typed consumption by SDK integrators. The SDK never fetches OTO state
// directly — it only relays sanitized lifecycle events emitted by the
// mounted checkout iframe (see `post_purchase_offer` / `post_purchase_decision`
// / `post_purchase_terminal` in CheckoutEmbedEvent below). `offerRef` is an
// opaque reference — no product/plan identifiers, checkout tokens, cart
// secrets, payment client secrets, or buyer PII are ever included. The 3DS
// step-up authentication for an accepted offer happens entirely inside the
// COPE-owned iframe; the parent only ever learns `requires_action` as a status.

export type PostPurchaseOfferStepKind = 'upsell' | 'downsell'

/**
 * Buyer's response to a presented offer. `requires_action` means a 3DS
 * step-up is in progress inside the COPE iframe — the payment client secret
 * never leaves that surface.
 */
export type PostPurchaseDecisionStatus = 'accepted' | 'declined' | 'failed_not_charged' | 'requires_action'

/** Payment outcome for an accepted offer, tracked separately from the buyer-facing decision status. */
export type PostPurchaseChargeStatus = 'not_charged' | 'charged' | 'requires_action' | 'failed'

/** Final state of the overall post-purchase offer flow. */
export type PostPurchaseTerminalStatus = 'completed' | 'declined' | 'expired' | 'unavailable'

/** Sanitized offer display fields — no product/plan IDs, only an opaque `offerRef`. */
export interface PostPurchaseOffer {
  readonly offerRef: string
  readonly stepKind: PostPurchaseOfferStepKind
  readonly title: string
  readonly priceGrossMinorUnits: number
  readonly currency: string
}

/** Sanitized decision outcome for a single offer. */
export interface PostPurchaseOutcome {
  readonly offerRef: string
  readonly status: PostPurchaseDecisionStatus
  readonly chargeStatus: PostPurchaseChargeStatus
}

export type PostPurchaseOfferPayload = PostPurchaseOffer
export type PostPurchaseDecisionPayload = PostPurchaseOutcome

export interface PostPurchaseTerminalPayload {
  readonly status: PostPurchaseTerminalStatus
}

export type CheckoutEmbedEvent =
  | ({ readonly type: 'ready' } & CheckoutEmbedEventMetadata)
  | ({ readonly type: 'error'; readonly payload: CheckoutEmbedErrorPayload } & CheckoutEmbedEventMetadata)
  | ({ readonly type: 'terminal'; readonly payload: CheckoutEmbedTerminalPayload } & CheckoutEmbedEventMetadata)
  | ({ readonly type: 'resize'; readonly payload: CheckoutEmbedResizePayload } & CheckoutEmbedEventMetadata)
  | ({ readonly type: 'post_purchase_offer'; readonly payload: PostPurchaseOfferPayload } & CheckoutEmbedEventMetadata)
  | ({ readonly type: 'post_purchase_decision'; readonly payload: PostPurchaseDecisionPayload } & CheckoutEmbedEventMetadata)
  | ({ readonly type: 'post_purchase_terminal'; readonly payload: PostPurchaseTerminalPayload } & CheckoutEmbedEventMetadata)

export interface CheckoutMountMessage {
  readonly type: 'copecart.checkout.mount'
  readonly version: 1
  readonly token: string
}

export interface MountCheckoutOptions {
  readonly target: HTMLElement | string
  readonly title?: string
  readonly className?: string
  /**
   * What to do when the iframe never completes the trusted ready handshake.
   * Defaults to `error`; set `redirect` for a buyer-safe hosted checkout fallback.
   */
  readonly fallback?: CheckoutEmbedFallbackMode
  readonly readyTimeoutMs?: number
  /**
   * Called after a trusted `cope.checkout` ready message from the mounted checkout iframe.
   * `mountCheckout()` intentionally does not expose checkout tokens, cart secrets,
   * payment client secrets, or buyer PII through any callback payload.
   */
  readonly onReady?: () => void
  readonly onResize?: (payload: CheckoutEmbedResizePayload) => void
  readonly onError?: (payload: CheckoutEmbedErrorPayload) => void
  readonly onTerminal?: (payload: CheckoutEmbedTerminalPayload) => void
  readonly onSuccess?: () => void
  readonly onCancel?: () => void
  readonly onFallbackRedirect?: () => void
  /**
   * Called when the mounted checkout iframe presents a post-purchase offer
   * (native OTO upsell/downsell). The payload is sanitized: display fields
   * and an opaque `offerRef` only — never a product/plan ID, checkout token,
   * cart secret, payment client secret, or buyer PII.
   */
  readonly onPostPurchaseOffer?: (payload: PostPurchaseOfferPayload) => void
  /**
   * Called when the buyer decides on a post-purchase offer. `requires_action`
   * means a 3DS step-up is happening inside the COPE iframe — the payment
   * client secret is never forwarded here.
   */
  readonly onPostPurchaseDecision?: (payload: PostPurchaseDecisionPayload) => void
  /** Called when the overall post-purchase offer flow reaches a final state. */
  readonly onPostPurchaseTerminal?: (payload: PostPurchaseTerminalPayload) => void
  readonly onMessage?: (event: CheckoutEmbedEvent) => void
}

export type MountCheckoutCallbacks = Omit<MountCheckoutOptions, 'target'>

export interface MountedCheckout {
  /**
   * Hosted checkout iframe. The SDK sets `allow="payment *"` and
   * `referrerPolicy="no-referrer"`.
   *
   * The SDK intentionally omits a `sandbox` attribute: no sandbox token set has
   * been proven compatible with all required wallet, redirect, and payment
   * provider flows yet. Embedding is instead constrained by the registered
   * `embed_origin` contract plus strict postMessage origin/source checks.
   */
  readonly iframe: HTMLIFrameElement
  destroy(): void
}
